import React, { useEffect, useState } from 'react';
import { Box, Grid, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody, Avatar } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  Bar
} from 'recharts';
import { formatDate, MeetingStatus } from '../../../utils/dashboardFormatters';
import ApiService from '../../../services/api';
import logger from '../../../utils/logger';
import { unwrapApi, extractArray } from '../../../utils/apiHelpers';

const Card = styled(Paper)(({ theme }) => ({
  borderRadius: 8,
  padding: theme.spacing(2),
  boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 360
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: 16,
  marginBottom: theme.spacing(2)
}));

const StatusPill = styled('span')(({ theme, status }) => {
  const colorMap = {
    [MeetingStatus.CONFIRMED]: { bg: 'rgba(52,168,83,0.15)', fg: '#34a853' },
    [MeetingStatus.SCHEDULED]: { bg: 'rgba(11,145,214,0.15)', fg: '#0b91d6' }
  };
  const c = colorMap[status] || { bg: 'rgba(160,160,170,0.15)', fg: '#60646c' };
  return {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    backgroundColor: c.bg,
    color: c.fg
  };
});

const StatsRow = ({ items = [] }) => (
  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
    {items.map((it, i) => (
      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fff', px: 1.25, py: 0.5, borderRadius: 1, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: it.color || '#0b91d6' }} />
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{it.status}: <span style={{ fontWeight: 700 }}>{it.count}</span></Typography>
      </Box>
    ))}
  </Box>
);

const UpcomingMeetingsSection = ({ upcomingMeetings: incomingMeetings = [], targetVsAchieved = [], meetingDetails = [] }) => {
  const [meetings, setMeetings] = useState(Array.isArray(incomingMeetings) ? incomingMeetings : []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Derived stats from fetched meetings
  const stats = React.useMemo(() => {
    const total = Array.isArray(meetings) ? meetings.length : 0;
    const lc = (s) => (s || '').toString().toLowerCase();
    let confirmed = 0, scheduled = 0, completed = 0, tele = 0, upcomingWeek = 0;
    const now = new Date();
    const weekLater = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    (meetings || []).forEach((m) => {
      const status = lc(m.status || m.meeting_status || m.meetingStatus || m.state || '');
      if (status.includes('confirm')) confirmed += 1;
      if (status.includes('scheduled')) scheduled += 1;
      if (status.includes('completed')) completed += 1;

      const telly = m.telly_meeting === true || m.telly_meeting === 1 || m.telly_meeting === '1' || m.tele === true || m.is_tele === true;
      if (telly) tele += 1;

      const dtRaw = m.date || m.date_time || m.meetingDate || m.meeting_date || m.start_date || m.created_at || m.createdAt || m.updatedAt || null;
      if (dtRaw) {
        try {
          const d = new Date(dtRaw);
          if (!Number.isNaN(d.getTime()) && d >= now && d <= weekLater) upcomingWeek += 1;
        } catch (_) { }
      }
    });

    return { total, confirmed, scheduled, completed, tele, upcomingWeek };
  }, [meetings]);

  useEffect(() => {
    // If parent passed meetings, use them. Otherwise attempt to fetch independently.
    if (Array.isArray(incomingMeetings) && incomingMeetings.length > 0) {
      setMeetings(incomingMeetings);
      return;
    }

    let mounted = true;
    const fetchNow = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await ApiService.get('/admin/meeting/get-meeting');
        const root = unwrapApi(resp);
        const fetched = extractArray(root, 'meetings', 'rows');

        // Debug: log fetched meetings so devs can compare with meeting listing
        console.log('[UpcomingMeetingsSection] fetched meetings:', Array.isArray(fetched) ? fetched.length : 'no-array', fetched && fetched.slice ? fetched.slice(0, 3) : fetched);
        if (mounted) {
          setMeetings(fetched || []);
        }
      } catch (err) {
        logger.warn('[UpcomingMeetingsSection] Failed to fetch meetings:', err?.message || err);
        if (mounted) setError(err?.message || String(err));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    // Initial fetch
    fetchNow();

    // Poll every 30 seconds for realtime-ish updates
    const interval = setInterval(() => {
      fetchNow();
    }, 30 * 1000);

    return () => { mounted = false; clearInterval(interval); };
  }, [JSON.stringify(incomingMeetings)]);

  return (
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={1} alignItems="stretch">
        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%' }}>
            <SectionTitle>Upcoming Meetings</SectionTitle>
            {meetingDetails && meetingDetails.length > 0 && (
              <StatsRow items={meetingDetails} />
            )}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Box sx={{ bgcolor: '#fff', px: 2, py: 1, borderRadius: 1, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', minWidth: 140 }}>
                <Typography sx={{ fontSize: 12, color: '#6b7280' }}>Total Meetings</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{stats.total}</Typography>
              </Box>
              <Box sx={{ bgcolor: '#fff', px: 2, py: 1, borderRadius: 1, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', minWidth: 140 }}>
                <Typography sx={{ fontSize: 12, color: '#6b7280' }}>Confirmed</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#34a853' }}>{stats.confirmed}</Typography>
              </Box>
              <Box sx={{ bgcolor: '#fff', px: 2, py: 1, borderRadius: 1, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', minWidth: 140 }}>
                <Typography sx={{ fontSize: 12, color: '#6b7280' }}>Scheduled</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#0b91d6' }}>{stats.scheduled}</Typography>
              </Box>
              <Box sx={{ bgcolor: '#fff', px: 2, py: 1, borderRadius: 1, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', minWidth: 140 }}>
                <Typography sx={{ fontSize: 12, color: '#6b7280' }}>Completed</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#7b95ff' }}>{stats.completed}</Typography>
              </Box>
              <Box sx={{ bgcolor: '#fff', px: 2, py: 1, borderRadius: 1, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', minWidth: 140 }}>
                <Typography sx={{ fontSize: 12, color: '#6b7280' }}>Tele Meetings</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#3b82f6' }}>{stats.tele}</Typography>
              </Box>
              <Box sx={{ bgcolor: '#fff', px: 2, py: 1, borderRadius: 1, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', minWidth: 140 }}>
                <Typography sx={{ fontSize: 12, color: '#6b7280' }}>Next 7 Days</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#ef4444' }}>{stats.upcomingWeek}</Typography>
              </Box>
            </Box>
            <Table size="small" sx={{
              '& th, & td': { py: 1, px: 1.75, fontSize: 13, whiteSpace: 'nowrap' },
              '& tbody tr': { height: 40 }
            }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '60%' }}>Client Name</TableCell>
                  <TableCell sx={{ width: 160 }}>Date</TableCell>
                  <TableCell sx={{ width: 160 }}>Meeting Time</TableCell>
                  <TableCell align="right" sx={{ width: 150 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={4}>Loading meetings...</TableCell>
                  </TableRow>
                )}
                {error && (
                  <TableRow>
                    <TableCell colSpan={4}>Failed to load meetings: {error}</TableCell>
                  </TableRow>
                )}
                {!loading && !error && meetings.slice(0, 12).map((m, idx) => {
                  const lead = m.lead || m.leadDetails || m.lead_details || {};
                  const clientDetails = m.clientDetails || m.client_details || m.client || {};
                  const userDetails = m.userDetails || m.user_details || {};
                  const clientName = m.clientName || m.client_name || m.profileName || m.profile_name || lead.business_name || clientDetails.full_name || userDetails.full_name || (clientDetails.name) || (m.client && (m.client.name || m.client.full_name)) || 'Client';
                  const dateVal = m.date || m.date_time || m.meetingDate || m.meeting_date || m.start_date || m.created_at || m.createdAt || m.updatedAt || null;
                  const timeVal = m.meetingTime || m.meeting_time || m.start_time || m.time || dateVal || null;
                  const statusVal = m.status || m.meeting_status || m.meetingStatus || m.state || 'Unknown';
                  const meetingId = m.id || m.meeting_id || m._id || m.uuid || m.meetingId || m.meeting_id_str;
                  return (
                    <TableRow key={idx} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }} onClick={() => { if (meetingId) navigate(`/admin-dashboard/meeting/${meetingId}`); }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 30, height: 30, bgcolor: '#e8f6ff', color: '#0b91d6' }}>{String(clientName).charAt(0) || 'C'}</Avatar>
                          <Typography color="primary" fontWeight={600} sx={{ cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {clientName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{formatDate(dateVal)}</TableCell>
                      <TableCell>{formatDate(timeVal)}</TableCell>
                      <TableCell align="right">
                        <StatusPill status={statusVal}>{statusVal}</StatusPill>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <SectionTitle>Given Target vs Achieved</SectionTitle>
            <Box sx={{ height: 360, width: 590 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={targetVsAchieved} barCategoryGap={"10%"} barGap={2} margin={{ left: 0, right: 8, top: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Bar dataKey="target" name="Target" fill="#7b95ff" barSize={20} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="achieved" name="Achieved" fill="#34a853" barSize={20} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UpcomingMeetingsSection;
